import React, {FC, useEffect, useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import { icons } from "../../../icons";
import { Button, ModalVariables, StartPage, TopProgress, SearchComponent, FilterBar} from "../../../components";
import Illustration from './illustration.svg'
import IllustrationDark from './illustration-dark.svg'
import { useNavigate } from "react-router";
import { networkService, routerService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import {
  StateWrapper,
  GreenDot,
  Greentext,
} from './styles'
import { useRouterCustom } from "../../../context/routerContext";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { useAuth } from "../../../context/user.context";
import { handleStatusName } from "../../../utils/handle-status-name";
import rootConfig from "../../../config";
import { compareArrays } from "../../../utils/findIntersection";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { usePageTitle } from "../../../context/navigationContext";

const ROUTER_FILTER_KEYS = [
  'status',
  'admin_state_up',
  'region',
  'availability_zones__contains',
  'distributed',
  'enable_snat',
];
const DEFAULT_ORDERING = '-created_at';

const Routers:FC = () => {
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);
  const [t] = useTranslation()
  usePageTitle(t("PAGE_TITLE_ROUTERS"));
  const navigate = useNavigate()
  const {
    viewAlert,
    setViewModalComplete,
    setActiveAnimationComplete,
  } = useUi()
  const {user, quotas} = useAuth()
  const {
    setRouterOptions,
    setInterfaceOptions,
    routers,
    setRouters,
  } = useRouterCustom()
  // const [routers, setRouters] = useState<any[]>([])
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingCreate, setLoadingCreate] = useState<boolean>(false)
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [checked, setChecked] = useState<any[]>([])
  const [alertTitle, setAlertTitle] = useState<any>()
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [paginationData, setPaginationData] = useState<any>()
  const [deletedRouter, setDeletedRouter] = useState<any>()
  const [editedRouter, setEditedRouter] = useState<any>()
  const [filterOptions, setFilterOptions] = useState<any>()
  const normalizedFilterOptions = useMemo(() => {
    if (!filterOptions) return undefined;
    const base: Record<string, any> = {};
    ROUTER_FILTER_KEYS.forEach((key) => {
      if (Array.isArray(filterOptions?.[key]) && filterOptions?.[key]?.length) {
        base[key] = filterOptions[key];
      }
    });
    // Fallback for boolean filters
    const booleanOptions = [
      { display: t("YES"), value: 'true' },
      { display: t("NO"), value: 'false' },
    ];
    ['admin_state_up', 'distributed', 'enable_snat'].forEach((key) => {
      if (!base[key]) {
        base[key] = booleanOptions;
      }
    });
    return base;
  }, [filterOptions, t]);
  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
    setAllChecked(false);
    setChecked([]);
  }
  const editChecked = (id:any, isAll?:boolean) => {
    if(id === null) {
      setChecked([])
      return
    }
    const founded = checked.find((item:any) => item.id === id?.id)
    if(founded) {
      if(isAll){ 
        return 
      } else {
        setChecked((prev:any) => {
          return prev.filter((item:any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  } 
  useEffect(() => {
    if(compareArrays(routers, checked) && checked?.length !== 0){
      setAllChecked(true)
    }
  },[checked])
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);

  const updateRouters = () => {
    // setLoading(true)
    routerService.getRouters({
      page:page||1,
      ordering: ordering || DEFAULT_ORDERING,
      pageSize:pageSize||10,
      search:searchString||'',
      filtering: filtering || '',
    })
    .then((res) => {
      setFilterOptions(res?.data?.filter_options)
      setPaginationData(res.data)
      setRouters(res.data.objects.map((router:any) => {
        return {
          ...router,
          name:router.name,
          created_at: timeConverterMMDDYYHHMM(router.created_at),
          status: 
          (<StateWrapper>
            <GreenDot active={router.status === "ACTIVE"}/>
            <Greentext active={router.status === "ACTIVE"}>
              {handleStatusName(router.status, t)}
            </Greentext>
          </StateWrapper>) ,
          network_name:router.network_name,
          region:router.region,
          navigate:router.id,
          id:router.id,
          
        }
      }))
    })
    .catch((e) => {
      viewAlert({
        severity:'error',
        message:e?.response?.data?.detail || `${t("ERROR_OCCURRED")}`,
      })
      navigate('/home')
    })
    .finally(() => {
      setPageLoading(false)
      // setLoading(false)
      // setActiveAnimation(false)
      // setViewModal(false)
    })
  }
  useEffect(() => {
    routerService.createOptionsRouter()
    .then((res) => {
      setRouterOptions(res.data)
      networkService.getSubnets()
      .then((res) => {
        setInterfaceOptions(res.data.objects)
      })
    })
  },[])
  useEffect(() => {
    updateRouters()
    const interval = setInterval(() => {
      updateRouters()
    },10000)
    return () => {
      clearInterval(interval)
    }
  },[page,
    ordering,
    pageSize,
    searchString,
    filtering])
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])
  const openModal = (modal:any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }
  const _deleteRouters = async () => {
    setLoading(true)
    try{
      if(allChecked) {
        let promises:any[] = []
        await routers.map(async(item:any) => {
          let promise = await routerService.deleteRouter(item.id)
          await promises.push(promise)
        })
        await Promise.all(promises)
        await updateRouters()
        await setAllChecked(false)
      } else if (checked?.length > 0){
        let promises:any[] = []
        await checked.map((item:any) => {
            
          let promise = routerService.deleteRouter(item.id)
          promises.push(promise)
        })
        await Promise.all(promises)
        await updateRouters()
        await setChecked([])
      } else if (deletedRouter) {
          
        await routerService.deleteRouter(deletedRouter.id)
        await updateRouters()
      }
    } catch (e:any) {
      if(e?.response?.data.detail){
        viewAlert({
          severity:'error',
          message:e.response.data.detail,
        })
      }
      updateRouters()
      setPageLoading(false)
    } finally {
      setViewModal(false)
      setActiveAnimation(false)
      setLoading(false)
    }
  }
  const closeModal = (props:any) => {
    if(modalType === 'delete'){
      _deleteRouters()
    } else {
      if(editedRouter) {
        routerService.checkRouter(props.data.name)
        .then((res:any) => {
          // if(!res.data.router) {
            setLoadingCreate(true)
            routerService.editRouter(props.data.id, props.data)
            .then((res) => {
              viewAlert({
                severity:'info',
                message:`Router ${props.data.name} updated`
              })
              updateRouters()
              setLoadingCreate(false)
              props.handleClose()
              setActiveAnimation(false)
              setViewModal(false)
            })
          // } else {
          //   props.setError({
          //     name:`Router ${props.data.name} already exists`
          //   })
          //   setLoadingCreate(false)
          // }
        })
        .catch((err) => {
          if(err?.response?.data?.detail){
            viewAlert({
              severity:'error',
              message:err.response.data.detail,
            })
          }
          setLoadingCreate(false)
        })
      } else {
        setLoadingCreate(true)
        routerService.checkRouter(props.data.name)
        .then((res:any) => {
          if(!res.data.router) {
            routerService.createRouter(props.data)
            .then(async(res:any) => {
              viewAlert({
                severity:'info',
                message:`${t("ROUTER_CREATED_SUCCESSFULLY")}`
              })
              routerService.checkRouter(res.data.name)
              .then(async(router) => {
                try{
                  let promises:any[] = []
                  await props.subnets.map((sub:any) => {
                    let subData = {
                      ip:'',
                      // ip:sub.cidr.indexOf('0/24') !== -1 ? sub.cidr.replace('0/24', '1') : sub.cidr.replace('/24', ''),
                      subnet:sub.value
                    }
                    let promise = routerService.addInterfaceToRouter(router.data.router, subData)
                    promises.push(promise)
                  })
              
                  await Promise.all(promises)
                  await props.handleClose()
                  await updateRouters()
                  await setLoadingCreate(false)
                  // break
                } catch (e:any){
                    viewAlert({
                      severity:'error',
                      message:e?.response?.data?.detail || `${t("ERROR_OCCURRED")}`,
                    })
                    await props.handleClose()
                    await updateRouters()
                    await setLoadingCreate(false)
                    // break
                } finally {
                  setActiveAnimation(false)
                  setViewModal(false)
                }
              })
            })
            .catch((err) => {
              if(err?.response?.data?.detail){
                viewAlert({
                  severity:'error',
                  message:err?.response?.data?.detail || `${t("ERROR_OCCURRED")}`,
                }) 
              } else { 
                props.setErrors(err.response?.data)
                setLoadingCreate(false)
              }
            })
          } else {
            props.setError({
              name:`Router ${props.data.name} already exists`
            })
            setLoadingCreate(false)
          }
          
        })
      }
    }
  }
  const closeModalCancel = () => {
    setLoadingCreate(false)
    setViewModal(false)
    setEditedRouter(null)
    setActiveAnimation(false)
  }

  const items = [
    {
      title:t("EDIT"),
      id:'2',
      icon:icons.Edit,
      onClick: (router:any) => {
        openModal('create-router')
        setEditedRouter(router)
      },
      divider:false
    },
    {
      title:t("DELETE"),
      id:'4',
      onClick:(router:any) => {
        openModal('delete')
        setAlertTitle(t("ROUTER"))
        setAlertSubTitle([router])
        setDeletedRouter(router)
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]
  const Columns = [
    {
      title:t("NAME"),
      key:'name',
      sorted:true,
    },
    {
      title:t("STATUS"),
      key:'status',
    },
    {
      title:t("NETWORK_NAME"),
      key:'network_name',
      sorted:true,
    },
    {
      title:t("REGION"),
      key:'region',
      sorted:true
    },
    {
      title:t("CREATED_AT"),
      key:'created_at',
      sorted:true
    },
    {
      title:'',
      key:'props'
    },
  ]
  const _createFunc = () => {
    openModal('create-router')
  }
  return (
    <>
      <TopProgress loading={pageLoading}/>
      <StartPage
        title={`${t("ROUTERS")}`}
        noResourceText={`${t("YOU_HAVE_NO_ROUTERS")}`}
        createResourceButton={`${t('CREATE_ROUTER')}`}
        createResourceButtonEventOrLink={_createFunc}
        haveResource={routers?.length > 0}
        learnMoreAboutResource={`${t('LEARN_MORE_ROUTERS')}`}
        howCreateVideoText={`${t('HOW_CREATE_ROUTER')}`}
        learnMoreAboutResourceLink={`${rootConfig.docs_url}category/routers`}
        howCreateVideoUrl="https://www.youtube.com/embed/XSceiIpejm4"
        pageLoading={pageLoading}
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              <Button
                title={t('CREATE_ROUTER')}
                onClick={_createFunc}
                variant='primary'
                size='custom'
                customStyles={{height:'40px', whiteSpace:'nowrap'}}
              />
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginLeft:'auto'}}>
                <Button
                  onClick={() => {
                    setAlertTitle(t("ROUTERS"))
                    setAlertSubTitle(allChecked ? routers : checked)
                    openModal('delete')
                  }}
                  variant='icon'
                  size='display'
                  test_id='button-delete'
                  disabled={!allChecked && checked?.length === 0 }
                >
                  <img src={icons.DeleteIcon}/>
                </Button>
                <Button
                  title={t("CLEAR_FILTERS")}
                  onClick={handleResetFilters}
                  variant='stroke'
                  size='display'
                  disabled={
                    (!filtering || filtering === '') &&
                    !searchString &&
                    ordering === DEFAULT_ORDERING
                  }
                />
              </div>
            </div>
            {normalizedFilterOptions ? (
              <FilterBar filterOptions={normalizedFilterOptions} />
            ) : null}
          </div>
        }
        videoImage={Illustration}
        videoImageDark={IllustrationDark}
        tableData={{
          columns:Columns,
          data:routers,
          props:items,
          allChecked:allChecked,
          setAllChecked:setAllChecked,
          checked:checked,
          setChecked:editChecked,
          paginationData:paginationData,
          updateData:updateRouters,
          // loading:loading,
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        loading={modalType === 'create-router' ? loadingCreate : loading}
        alertSubTitle={alertSubTitle}
        dataToEdit={editedRouter}
      />
    </>
  )
}


export default Routers
